/* eslint-disable react/no-array-index-key */
/*
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import classnames from 'classnames';
import React, {
  useMemo, useState, useRef, useCallback, useEffect
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  IlAccordion, IlAccordionItem, IlButton, IlIcon, IlTooltipTippy
} from 'il-framework-component';
import { I18nNamespaces, FactorStatuses, LoaElevationActions } from '../../../app/shared/utils/constants.util';
import { RootState } from '../../../app/store';
import { ISecurityFactor } from '../../../domain/models/ISecuritySetup';
import { SecuritySetupType } from '../../../domain/models/impl/SecuritySetup';
import padlockIcon from '../../../assets/padlock.svg';
import googlePlayBadge from '../../../assets/google-play-badge.svg';
import appleStoreBadge from '../../../assets/apple-store-badge.svg';
import common from '../../../scss/Common.scss';
import styles from './LoAFactors.scss';
import { capitalizeFirstLetter, maskEmailFixed } from '../../../app/shared/utils/helpers.util';
import {
  resetProfileEnrollState, ProfileSelectedFactor
} from './profileEnrollSlice';
import ProfileEnrollAuthenticator from './Methods/Authenticator/ProfileEnrollAuthenticator';
import ProfileEnrollSMS from './Methods/SMS/ProfileEnrollSMS';
import ProfileEnrollEmail from './Methods/Email/ProfileEnrollEmail';
import DeactivateFactorModal from './DeactivateFactorModal';
import LoaElevationModal from '../../../app/shared/components/LoaElevationModal/LoaElevationModal';
import LoaElevatingOverlay from '../../../app/shared/components/LoaElevatingOverlay/LoaElevatingOverlay';
import { useLoaElevation } from '../../../app/shared/hooks/useLoaElevation';

/** Max active factors allowed per type. */
const MAX_ACTIVE: Record<string, number> = {
  [SecuritySetupType.AUTHENTICATOR]: 3,
  [SecuritySetupType.SMS]: 3,
  [SecuritySetupType.EMAIL]: 1
};

const SECURITY_LABEL: Record<number, string> = {
  3: 'High',
  2: 'Medium',
  1: 'Low'
};

const LoAFactors: React.FC = () => {
  const { t } = useTranslation(I18nNamespaces.LOA_ENRICHMENT);
  const dispatch = useDispatch();
  const enrolledFactors = useSelector((state: RootState) => state.setupSecurity.enrolledFactors);
  const supportedFactors = useSelector((state: RootState) => state.setupSecurity.supportedFactors);
  const profileEmail = useSelector((s: RootState) => s.profile.profileDetails?.externalId);
  const selectedFactor = useSelector((s: RootState) => s.profileEnroll.selectedFactor);
  const [enrollingType, setEnrollingType] = useState<SecuritySetupType | null>(null);
  const [deactivatingFactor, setDeactivatingFactor] = useState<ISecurityFactor | null>(null);
  const loaElevation = useLoaElevation();
  const isBusy = !!enrollingType || !!deactivatingFactor;

  const factorGroups = useMemo(
    () => supportedFactors.map((sf) => ({
      supported: sf,
      active: enrolledFactors.filter(
        (ef) => ef.type === sf.type && (ef.status ?? '').toUpperCase() === FactorStatuses.ACTIVE
      ),
      maxActive: MAX_ACTIVE[sf.type] ?? 1
    })),
    [supportedFactors, enrolledFactors]
  );

  const totalActiveCount = useMemo(
    () => enrolledFactors.filter(
      (ef) => (ef.status ?? '').toUpperCase() === FactorStatuses.ACTIVE
    ).length,
    [enrolledFactors]
  );

  const typeLabel: Record<string, string> = {
    [SecuritySetupType.AUTHENTICATOR]: t('loaEnrichment.profile.authTitle'),
    [SecuritySetupType.SMS]: t('loaEnrichment.profile.smsTitle'),
    [SecuritySetupType.EMAIL]: t('loaEnrichment.profile.emailTitle')
  };

  const getFactorDetail = (factor: ISecurityFactor): string => {
    switch (factor.type) {
      case SecuritySetupType.SMS:
        return factor.options?.phoneNumber || '';
      case SecuritySetupType.EMAIL:
        return maskEmailFixed(factor.options?.email || profileEmail || '');
      default:
        return '';
    }
  };

  useEffect(() => () => {
    dispatch(resetProfileEnrollState());
  }, [dispatch]);

  /* Resume add-factor enrollment after redirect-mode LoA elevation return. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loaAction = params.get('loaAction');
    const factorTypeRaw = params.get('factorType')?.toUpperCase() as SecuritySetupType | undefined;
    if (
      loaAction === 'add'
      && factorTypeRaw
      && Object.values(SecuritySetupType).includes(factorTypeRaw)
    ) {
      params.delete('loaAction');
      params.delete('factorType');
      const qs = params.toString();
      const cleanUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
      window.history.replaceState(null, '', cleanUrl);
      dispatch(resetProfileEnrollState());
      setEnrollingType(factorTypeRaw);
    }
  }, []);

  /* Close the enrollment section when a 404 resets selectedFactor. */
  const prevSelectedRef = useRef<ProfileSelectedFactor | undefined>(undefined);
  useEffect(() => {
    if (enrollingType && prevSelectedRef.current && !selectedFactor) {
      setEnrollingType(null);
    }
    prevSelectedRef.current = selectedFactor;
  }, [selectedFactor, enrollingType]);

  const handleAdd = useCallback((type: SecuritySetupType) => {
    loaElevation.requireElevation(LoaElevationActions.add, () => {
      dispatch(resetProfileEnrollState());
      setEnrollingType((prev) => (prev === type ? null : type));
    }, { redirectParams: { loaAction: 'add', factorType: type } });
  }, [dispatch, loaElevation]);

  const handleEnrolled = useCallback(() => {
    dispatch(resetProfileEnrollState());
    setEnrollingType(null);
  }, [dispatch]);

  const handleCancel = useCallback(() => {
    dispatch(resetProfileEnrollState());
    setEnrollingType(null);
  }, [dispatch]);

  const handleDeactivate = useCallback((factor: ISecurityFactor) => {
    setDeactivatingFactor(factor);
  }, []);

  const enrollingLoaLevel = useMemo(() => {
    if (!enrollingType) return 2;
    return supportedFactors.find((sf) => sf.type === enrollingType)?.loaLevel ?? 2;
  }, [enrollingType, supportedFactors]);

  if (enrollingType) {
    return (
      <div className={classnames(common.ilForm, styles.enrollSection)}>
        <div className={styles.enrollHeader}>
          <span className={styles.enrollSectionLabel}>
            {typeLabel[enrollingType] || ''}
          </span>
          <button
            type='button'
            className={styles.enrollCloseBtn}
            onClick={handleCancel}
            aria-label='Close'
          >
            <span className={styles.enrollCloseIcon}>
              <IlIcon name='close' />
            </span>
          </button>
        </div>
        <div className={styles.enrollWrapper}>
          {enrollingType === SecuritySetupType.AUTHENTICATOR && (
            <ProfileEnrollAuthenticator
              onEnrolled={handleEnrolled}
              onCancel={handleCancel}
            />
          )}
          {enrollingType === SecuritySetupType.SMS && (
            <ProfileEnrollSMS
              loaLevel={enrollingLoaLevel}
              onEnrolled={handleEnrolled}
              onCancel={handleCancel}
            />
          )}
          {enrollingType === SecuritySetupType.EMAIL && (
            <ProfileEnrollEmail
              loaLevel={enrollingLoaLevel}
              onEnrolled={handleEnrolled}
              onCancel={handleCancel}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={common.ilForm}>
      <div>
        <h1 className={classnames(common.formTitle, styles.title)} id='profileTitle'>
          {t('loaEnrichment.profile.title')}
        </h1>
        <p id='profileSubtitle' className={classnames(common.helpText)}>
          {t('loaEnrichment.profile.subtitle')}
        </p>
      </div>

      <div className={styles.factorTypeContainer}>
        <IlAccordion align='start'>
          {factorGroups.map(({ supported, active, maxActive }) => {
            const count = active.length;
            const isMaxReached = count >= maxActive;
            const level = SECURITY_LABEL[supported.securityLevel] || SECURITY_LABEL[1];

            return (
              <IlAccordionItem
                key={supported.id}
                title={(
                  <div className={styles.accordionHeader} role='presentation'>
                    <div className={styles.headerLeft}>
                      <div className={styles.headerTopRow}>
                        <span className={styles.factorTypeName}>
                          {typeLabel[supported.type]}
                        </span>
                        <span className={styles.configuredCount}>
                          {t('loaEnrichment.profile.configuredCount', { count, max: maxActive })}
                        </span>
                      </div>
                      <div className={styles.headerBottomRow}>
                        <span className={styles.securityLevelLabel}>
                          {t('loaEnrichment.profile.textSecurityLevel')}
                        </span>
                        <span className={styles.securityIcons}>
                          {[...Array(supported.securityLevel)].map((_, i) => (
                            <img key={`${supported.id}-lock-${i}`} src={padlockIcon} alt='' />
                          ))}
                        </span>
                        <span className={styles.securityBadge}>
                          {level}
                        </span>
                      </div>
                      {supported.type === SecuritySetupType.AUTHENTICATOR && (
                        <div className={styles.authHelpInTitle}>
                          <p className={styles.authHelpText}>
                            {t('loaEnrichment.factors.authenticator.authenticatorsHelpText')}
                          </p>
                          <span className={styles.authAppsList}>
                            {[
                              t('loaEnrichment.factors.authenticator.apps.microsoft'),
                              t('loaEnrichment.factors.authenticator.apps.google'),
                              t('loaEnrichment.factors.authenticator.apps.oneLogin'),
                              t('loaEnrichment.factors.authenticator.apps.authy'),
                              t('loaEnrichment.factors.authenticator.apps.lastPass')
                            ].join(' \u00B7 ')}
                          </span>
                          <div className={styles.badgeContainer}>
                            <a
                              target='_blank'
                              href='https://play.google.com/store/apps/details?id=com.intralinks.sm'
                              rel='noreferrer'
                            >
                              <img src={googlePlayBadge} className={styles.badge} alt='Google Play' />
                            </a>
                            <a
                              target='_blank'
                              href='https://apps.apple.com/us/app/intralinks/id1129842754'
                              rel='noreferrer'
                            >
                              <img src={appleStoreBadge} className={styles.badge} alt='Apple Store' />
                            </a>
                          </div>
                        </div>
                      )}
                    </div>

                    <IlButton
                      className={styles.addButton}
                      kind='primary'
                      size='small'
                      disabled={isMaxReached || isBusy}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleAdd(supported.type);
                      }}
                    >
                      {t('loaEnrichment.common.buttons.add')}
                    </IlButton>
                  </div>
                )}
              >
                {active.length === 0 ? (
                  <p className={styles.emptyMessage}>
                    {t('loaEnrichment.profile.noMethodsConfiguredText')}
                  </p>
                ) : (
                  <ul className={styles.enrolledList}>
                    {active.map((factor) => (
                      <li key={factor.id} className={styles.enrolledItem}>
                        <div className={styles.factorInfo}>
                          {factor.type === SecuritySetupType.AUTHENTICATOR ? (
                            <>
                              <span className={styles.factorLabel}>
                                {t('loaEnrichment.profile.authenticatorIdentifier')}
                              </span>
                              <span className={styles.factorDetail}>
                                {capitalizeFirstLetter(factor.label as string) || ''}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className={styles.factorLabel}>
                                {capitalizeFirstLetter(factor.label as string) || ''}
                              </span>
                              {getFactorDetail(factor) && (
                                <span className={styles.factorDetail}>
                                  {getFactorDetail(factor)}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        <IlTooltipTippy
                          disabled={totalActiveCount > 1}
                          content={t('loaEnrichment.common.errors.cannotDeactivateLastFactor')}
                        >
                          <span>
                            <IlButton
                              kind='danger'
                              size='small'
                              disabled={isBusy || totalActiveCount <= 1}
                              onClick={() => handleDeactivate(factor)}
                            >
                              {t('loaEnrichment.common.buttons.deactivate')}
                            </IlButton>
                          </span>
                        </IlTooltipTippy>
                      </li>
                    ))}
                  </ul>
                )}

              </IlAccordionItem>
            );
          })}
        </IlAccordion>
      </div>

      <DeactivateFactorModal
        factor={deactivatingFactor}
        isOpen={!!deactivatingFactor}
        onClose={() => setDeactivatingFactor(null)}
        onDeactivated={() => setDeactivatingFactor(null)}
      />

      <LoaElevationModal
        isOpen={loaElevation.showModal}
        elevating={loaElevation.elevating}
        onContinue={loaElevation.handleContinue}
        onCancel={loaElevation.handleCancel}
      />

      {loaElevation.elevating && !loaElevation.showModal && (
        <LoaElevatingOverlay />
      )}
    </div>
  );
};

export default LoAFactors;
